// ActionsDataTable.jsx
import { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { modeConstants, recruiterConstants } from "../constants";
import { useDispatch, useSelector } from "react-redux";

export default function ActionsDataTable({
  columns,
  filterColumns,
  data,
  setGridState,
  setShowModal,
  paginationResetDefaultPage,
  paginationDefaultPage,
  onChangePage,
  onChangeRowsPerPage,
  paginationTotalRows,
  setMode,
  onSort,
  gridState,
  allowCreation = true,
  allowFilters = true,
  allowBulkActions = false,
  allowSelection = false,
  allowToolbar = true,
  sortServer = false,
  pagination = false,
  paginationServer = false,
  getAll,
}) {
  const dispatch = useDispatch();
  // ---------- Helpers ----------
  const getColMeta = (key) => filterColumns?.find((c) => c.key === key);
  const defaultOpFor = (type) =>
    type === "number" ? "EqualTo" : type === "date" ? "EqualTo" : "Contains";

  const stringOps = [
    { value: "Contains", label: "Contains" },
    { value: "StartsWith", label: "StartsWith" },
    { value: "EndsWith", label: "EndsWith" },
    { value: "EqualTo", label: "EqualTo" },
  ];
  const numberOps = [{ value: "EqualTo", label: "EqualTo" }];
  const dateOps = [
    { value: "EqualTo", label: "EqualTo" },
    { value: "Before", label: "Before" },
    { value: "After", label: "After" },
  ];
  const opsForType = (type) =>
    type === "number" ? numberOps : type === "date" ? dateOps : stringOps;

  const escapeForDQString = (s) =>
    String(s).replace(/\\/g, "\\\\").replace(/"/g, '\\"');

  // ---------- Filter rows with stable IDs ----------
  const idSeq = useRef(1);
  const makeRow = (columnKey = "", value = "", op = "Contains") => ({
    id: idSeq.current++,
    columnKey,
    value,
    op,
  });

  const [filters, setFilters] = useState([makeRow()]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const selectedKeys = (excludeId = null) =>
    filters.filter((r) => r.id !== excludeId).map((r) => r.columnKey);

  const remainingFor = (currentId) => {
    const used = selectedKeys(currentId);
    return (filterColumns || []).filter((c) => !used.includes(c.key));
  };

  const addFilter = () => {
    const remaining = remainingFor(null);
    if (!remaining.length) return;
    const first = remaining[0];
    const defOp = defaultOpFor(first.type || "string");
    setFilters((prev) => [...prev, makeRow(first.key, "", defOp)]);
  };

  const removeFilter = (rowId) =>
    setFilters((prev) => prev.filter((r) => r.id !== rowId));

  const updateFilter = (rowId, patch) =>
    setFilters((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r))
    );

  // ✅ changed here: no base64, store plain filters
  const applyFilters = () => {
    const rows = filters
      .map(({ id, ...rest }) => rest)
      .filter((f) => {
        return (
          f.columnKey &&
          f.value !== undefined &&
          f.value !== null &&
          String(f.value).trim() !== ""
        );
      });

    setGridState({
      ...gridState,
      filters: rows,
    });

    // if you want to fetch right away:
    // getAll && getAll({ ...gridState, filters: rows });
  };

  const resetFilters = () => {
    setFilters([makeRow()]);
    // also clear in parent if you want
    setGridState({
      ...gridState,
      filters: [],
    });
  };

  // ---------- Selection ----------
  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelectionToggle, setClearSelectionToggle] = useState(false);
  const onSelectedRowsChange = ({ selectedRows }) =>
    setSelectedRows(selectedRows);
  const clearSelection = () => setClearSelectionToggle((v) => !v);

  const bulkExport = () => {
    console.log("selectedRows", selectedRows);
  };

  const bulkDelete = () => {
    if (window.confirm(`Delete ${selectedRows.length} items?`)) {
      alert("Deleted (demo)");
      clearSelection();
    }
  };
  const onCreate = () => {
    setShowModal?.(true);
    setMode?.(modeConstants.ADD_MODE);
  };

  // ---------- Toolbar ----------
  const Toolbar = () => (
    <>
      {allowToolbar && (
        <div className="card shadow-sm mb-2 border-0">
          <div className="card-body py-2 px-3 d-flex flex-wrap align-items-center gap-2">
            {/* Selection */}
            <div
              className="d-flex align-items-center me-auto"
              style={{ gap: 8 }}
            >
              {allowSelection && (
                <span
                  className=" text-dark d-flex align-items-center"
                  style={{ gap: 6 }}
                >
                  <i className="bi bi-check2-square"></i>
                  <strong>Selection:</strong> {selectedRows.length}
                </span>
              )}
            </div>

            {/* Create */}
            {allowCreation && (
              <button
                className="btn btn-success d-flex align-items-center"
                onClick={onCreate}
              >
                <i className="bi bi-plus-lg me-2"></i>Create
              </button>
            )}

            {/* Bulk Actions */}
            {allowBulkActions && selectedRows.length >= 2 && (
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-outline-primary dropdown-toggle d-flex align-items-center"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="bi bi-list-check me-2"></i>Bulk Actions
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center"
                      onClick={bulkExport}
                    >
                      <i className="bi bi-download me-2"></i>Export Selected
                    </button>
                  </li>
                </ul>
              </div>
            )}

            {/* Filters */}
            {allowFilters && (
              <button
                className="btn btn-outline-secondary d-flex align-items-center"
                type="button"
                onClick={() => setFiltersOpen(true)}
              >
                <i className="bi bi-funnel me-2"></i>Filters
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div>
      <Toolbar />

      {/* Right Drawer + Overlay */}
      <div
        className={`drawer-overlay ${filtersOpen ? "show" : ""}`}
        onClick={() => setFiltersOpen(false)}
      />
      <div
        className={`drawer ${filtersOpen ? "show" : ""}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="drawer-header">
          <h5 className="m-0 d-flex align-items-center">
            <i className="bi bi-funnel me-2"></i>Filters
          </h5>
          <button
            className="btn btn-md btn-outline-light text-light"
            onClick={() => setFiltersOpen(false)}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="drawer-body">
          {filters.map((f, idx) => {
            const options = (() => {
              const othersUsed = selectedKeys(f.id);
              const currentCol = filterColumns.find(
                (c) => c.key === f.columnKey
              );
              const remaining = filterColumns.filter(
                (c) => !othersUsed.includes(c.key)
              );
              return currentCol
                ? [
                    currentCol,
                    ...remaining.filter((c) => c.key !== currentCol.key),
                  ]
                : remaining;
            })();

            const isLast = idx === filters.length - 1;
            const colMeta = getColMeta(f.columnKey);
            const colType = colMeta?.type || "string";
            const opOptions = opsForType(colType);

            return (
              <div key={f.id} className="row align-items-center mb-2">
                <div className="col-4">
                  {/* Column */}
                  <select
                    className="form-select"
                    value={f.columnKey}
                    onChange={(e) => {
                      const newKey = e.target.value;
                      const newType = getColMeta(newKey)?.type || "string";
                      const newDefaultOp = defaultOpFor(newType);
                      updateFilter(f.id, {
                        columnKey: newKey,
                        value: "",
                        op: newDefaultOp,
                      });
                    }}
                  >
                    <option value="">Select Column</option>
                    {options.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-4">
                  {/* Operator */}
                  <select
                    className="form-select"
                    value={f.op || defaultOpFor(colType)}
                    onChange={(e) => updateFilter(f.id, { op: e.target.value })}
                    disabled={!f.columnKey}
                  >
                    {opOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-3">
                  {/* Value */}
                  <input
                    type={
                      colType === "number"
                        ? "number"
                        : colType === "date"
                        ? "date"
                        : "text"
                    }
                    className="form-control"
                    value={f.value}
                    onChange={(e) =>
                      updateFilter(f.id, { value: e.target.value })
                    }
                    placeholder={
                      colType === "number"
                        ? "Enter number"
                        : colType === "date"
                        ? "Select date"
                        : "Enter value"
                    }
                    disabled={!f.columnKey}
                  />
                </div>
                <div className="col-1 p-0">
                  {/* + / − */}
                  {isLast ? (
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={addFilter}
                      disabled={remainingFor(null).length === 0}
                      title="Add filter"
                    >
                      +
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => removeFilter(f.id)}
                      title="Remove filter"
                    >
                      −
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          <div className="d-flex justify-content-end mt-3" style={{ gap: 8 }}>
            <button className="btn btn-outline-danger" onClick={resetFilters}>
              Clear
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                applyFilters();
              }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data}
        highlightOnHover
        striped
        responsive
        persistTableHead
        selectableRows={allowSelection}
        onSelectedRowsChange={onSelectedRowsChange}
        clearSelectedRows={clearSelectionToggle}
        selectableRowsHighlight
        selectableRowsVisibleOnly
        pagination={pagination}
        paginationServer={paginationServer}
        sortServer={sortServer}
        onSort={onSort}
        gridState={gridState}
        paginationTotalRows={paginationTotalRows}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        paginationDefaultPage={paginationDefaultPage}
        paginationResetDefaultPage={paginationResetDefaultPage}
      />
    </div>
  );
}
